// Date helpers for showing an event date in both the Hijri (Umm al-Qura) and Gregorian calendars.
// Mirrors the server's EventDateFormatter so the form, wallet passes and messages read the same.
//
// Dates travel as plain "YYYY-MM-DD" strings (Gregorian - the only thing we store). Internally a
// day is a UTC-midnight Date, so time zones and daylight saving can never shift it by one day.
// The Hijri date is always derived from the Gregorian one. An optional -1/0/+1 `adjustment`
// shifts only the *displayed* Hijri date.

export const CALENDAR_HIJRI = "hijri";
export const CALENDAR_GREGORIAN = "gregorian";

const DAY_MS = 86400000;

export const HIJRI_MONTHS_AR = ["محرم", "صفر", "ربيع الأول", "ربيع الآخر", "جمادى الأولى", "جمادى الآخرة", "رجب", "شعبان", "رمضان", "شوال", "ذو القعدة", "ذو الحجة"];
export const HIJRI_MONTHS_EN = ["Muharram", "Safar", "Rabi' al-Awwal", "Rabi' al-Thani", "Jumada al-Awwal", "Jumada al-Thani", "Rajab", "Sha'ban", "Ramadan", "Shawwal", "Dhu al-Qi'dah", "Dhu al-Hijjah"];
export const GREGORIAN_MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
export const GREGORIAN_MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const GREGORIAN_MONTHS_EN_SHORT = GREGORIAN_MONTHS_EN.map((m) => m.slice(0, 3));

export const WEEKDAYS_AR = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
export const WEEKDAYS_EN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const isArabicLang = (lang) => (lang || "").startsWith("ar");

const toArabicIndic = (n) => String(n).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);

// ---- plain ISO date <-> UTC day -------------------------------------------------------------

export const parseIso = (value) => {
  const m = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

export const toIso = (utcDate) => utcDate.toISOString().slice(0, 10);

export const addDays = (utcDate, days) => new Date(utcDate.getTime() + days * DAY_MS);

export const todayIso = () => {
  const n = new Date();
  return toIso(new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())));
};

// ---- Hijri (Umm al-Qura) via Intl ------------------------------------------------------------

let hijriFormatter;
const getHijriFormatter = () => {
  if (hijriFormatter === undefined) {
    try {
      hijriFormatter = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura-nu-latn", {
        day: "numeric", month: "numeric", year: "numeric", timeZone: "UTC",
      });
    } catch (e) {
      hijriFormatter = null;
    }
  }
  return hijriFormatter;
};

// False on a browser without Umm al-Qura support - the UI then falls back to Gregorian only.
export const isHijriSupported = () => {
  try {
    const p = hijriParts(new Date(Date.UTC(2026, 0, 1)));
    return !!p && p.year > 1400;
  } catch (e) {
    return false;
  }
};

// { year, month (1-12), day } of the Umm al-Qura date for a UTC day, or null.
export const hijriParts = (utcDate, adjustment = 0) => {
  const f = getHijriFormatter();
  if (!f) return null;
  const parts = f.formatToParts(addDays(utcDate, adjustment));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const year = get("year"), month = get("month"), day = get("day");
  return year && month && day ? { year, month, day } : null;
};

// First Gregorian day that carries the given (unadjusted) Hijri date. Starts from an estimate and
// corrects it by the remaining difference until the Intl conversion agrees.
const hijriToGregorianRaw = (year, month, day) => {
  const key = (y, m, d) => y * 372 + m * 31 + d;
  const target = key(year, month, day);
  let guess = addDays(new Date(Date.UTC(622, 6, 19)), Math.round((year - 1) * 354.36707 + (month - 1) * 29.530588 + (day - 1)));
  for (let i = 0; i < 40; i += 1) {
    const p = hijriParts(guess);
    if (!p) return null;
    const current = key(p.year, p.month, p.day);
    if (current === target) return guess;
    const estimate = Math.round((year - p.year) * 354.36707 + (month - p.month) * 29.530588 + (day - p.day));
    guess = addDays(guess, estimate !== 0 ? estimate : target > current ? 1 : -1);
  }
  return null;
};

// Gregorian day for a displayed Hijri date (the adjustment is undone so a picked date round-trips).
export const hijriToGregorian = (year, month, day, adjustment = 0) => {
  const g = hijriToGregorianRaw(year, month, day);
  return g ? addDays(g, -adjustment) : null;
};

// Length of a displayed Hijri month, and the Gregorian day its 1st falls on.
export const hijriMonthInfo = (year, month, adjustment = 0) => {
  const first = hijriToGregorian(year, month, 1, adjustment);
  if (!first) return null;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const next = hijriToGregorian(nextYear, nextMonth, 1, adjustment);
  const length = next ? Math.round((next - first) / DAY_MS) : 30;
  return { first, length };
};

// ---- Formatting ---------------------------------------------------------------------------------

export const formatGregorianDate = (utcDate, lang) =>
  isArabicLang(lang)
    ? `${utcDate.getUTCDate()} ${GREGORIAN_MONTHS_AR[utcDate.getUTCMonth()]} ${utcDate.getUTCFullYear()}م`
    : `${utcDate.getUTCDate()} ${GREGORIAN_MONTHS_EN_SHORT[utcDate.getUTCMonth()]} ${utcDate.getUTCFullYear()}`;

// Arabic: Arabic-Indic digits for Hijri (Gregorian stays Western). English: Western digits + "AH".
export const formatHijriDate = (utcDate, lang, adjustment = 0) => {
  const p = hijriParts(utcDate, adjustment);
  if (!p) return null;
  return isArabicLang(lang)
    ? `${toArabicIndic(p.day)} ${HIJRI_MONTHS_AR[p.month - 1]} ${toArabicIndic(p.year)}هـ`
    : `${p.day} ${HIJRI_MONTHS_EN[p.month - 1]} ${p.year} AH`;
};

// The calendar that leads: the event's own choice, else Hijri for Arabic and Gregorian otherwise.
export const resolvePrimaryCalendar = (preference, lang) =>
  preference === CALENDAR_HIJRI || preference === CALENDAR_GREGORIAN
    ? preference
    : isArabicLang(lang) ? CALENDAR_HIJRI : CALENDAR_GREGORIAN;

// { primary, secondary } text for a "YYYY-MM-DD" value. Secondary is null when Hijri is unavailable.
export const formatDualDate = (value, lang, preference, adjustment = 0) => {
  const date = parseIso(value);
  if (!date) return { primary: "", secondary: null };
  const gregorian = formatGregorianDate(date, lang);
  const hijri = formatHijriDate(date, lang, adjustment);
  if (!hijri) return { primary: gregorian, secondary: null };
  return resolvePrimaryCalendar(preference, lang) === CALENDAR_HIJRI
    ? { primary: hijri, secondary: gregorian }
    : { primary: gregorian, secondary: hijri };
};
